import { useMutation } from '@apollo/client/react'
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu'
import { toast } from 'react-toastify'

import { CommunityCardPreview } from '@/components/molecules/Cards/CommunityCard/CommunityCardPreview'
import { PrevNextNav } from '@/components/molecules/Pager/PrevNextNav'
import { StepperDiptychLayout } from '@/components/templates/StepperDiptychLayout'
import { StepWizardProvider } from '@/components/wizard/StepWizardContext'
import { NewCommunityDocument } from '@/graphql/api/generated'
import { useTracking } from '@/hooks/useTracking'
import { CommunitiesRoutes } from '@/routes/_hooks/routes.enum'
import { generatePath, useNavigate } from '@/routing'
import { base64ToFile } from '@/utils/image'

import { CommunityTypeField } from './steps/CommunityTypeField'
import { DetailsStep } from './steps/DetailsStep'
import { LocationStep } from './steps/LocationStep'
import { MediaStep } from './steps/MediaStep'
import { NameStep } from './steps/NameStep'
import { CircleWizardFormData } from './types'

type Step = {
  id: number
  title: string
  description: string
  component: React.ReactNode
}

type NewCircleWizardProps = {
  parent?: string
  title?: string
  onSuccess?: () => void
  /** Network IRIs to associate with the new community */
  networkIris?: string[]
  /** Show a live community-card preview alongside the form (standalone creation page). */
  showPreview?: boolean
  /**
   * `embedded` (default): compact layout used inside modals (network wizard,
   * sub-community, network communities manager).
   * `page`: full-screen diptych layout matching the network creation wizard.
   */
  variant?: 'page' | 'embedded'
}

/** Live preview fed by the wizard form values; rendered only when showPreview is set. */
const WizardLivePreview = () => {
  const { control } = useFormContext<CircleWizardFormData>()
  const values = useWatch({ control })

  return (
    <CommunityCardPreview
      name={values.name ?? ''}
      type={values.type ?? 'neighborhood'}
      customTypeLabel={values.customTypeLabel ?? ''}
      city={values.city ?? ''}
      logo={values.logo as CircleWizardFormData['logo']}
      cover={values.cover as CircleWizardFormData['cover']}
    />
  )
}

export const NewCircleWizard = (props: NewCircleWizardProps = {}) => {
  const {
    parent,
    title,
    onSuccess,
    networkIris,
    showPreview,
    variant = 'embedded',
  } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [createCommunity, { loading }] = useMutation(NewCommunityDocument)
  const { captureEvent } = useTracking()
  const methods = useForm<CircleWizardFormData>({
    defaultValues: {
      name: '',
      type: 'neighborhood',
      customTypeLabel: '',
      address: '',
      city: '',
      latitude: '',
      longitude: '',
      explicitLocationLabel: '',
      description: '',
      indexable: true,
      logo: undefined,
      cover: undefined,
    },
  })

  const handleSubmit = async ({
    logo,
    cover,
    removeLogo,
    removeCover,
    // `city` is preview-only; the API derives it from the full address.
    city: _city,
    ...input
  }: CircleWizardFormData) => {
    captureEvent('community_creation_started', {
      community_name: input.name,
      community_type: input.type,
      community_description: input.description,
      community_address: input.address,
      has_logo: !!logo,
      has_cover: !!cover,
    })

    try {
      const result = await createCommunity({
        variables: {
          input: {
            logo: logo ? base64ToFile(logo) : undefined,
            cover: cover ? base64ToFile(cover) : undefined,
            ...input,
            ...(parent && ({ parent } as { parent?: string })),
            ...(networkIris &&
              networkIris.length > 0 && { networks: networkIris }),
          } as any,
        },
      })

      if (result.data?.createCircle?.circle) {
        const createdCircle = result.data.createCircle.circle

        captureEvent('community_created', {
          community_id: createdCircle.id,
          community_slug: createdCircle.slug,
          community_name: input.name,
          community_type: input.type,
          community_description: input.description,
          community_address: input.address,
          has_logo: !!logo,
          has_cover: !!cover,
        })

        const communityShowPath = generatePath(CommunitiesRoutes.Show, {
          slug: createdCircle.slug,
        })

        if (onSuccess) {
          toast.success(
            <Box>
              <Text mb={2}>{t('toasts:communities.new.success')}</Text>
              <Button
                size="sm"
                colorPalette="blue"
                onClick={() => {
                  navigate(communityShowPath)
                  toast.dismiss()
                }}
              >
                {t('global:view', {
                  defaultValue: 'Voir la communauté',
                })}
              </Button>
            </Box>
          )
        } else {
          toast.success(t('toasts:communities.new.success'))
        }

        onSuccess?.()
        // Only navigate if onSuccess is not provided (default behavior)
        if (!onSuccess) {
          navigate(
            generatePath(CommunitiesRoutes.Edit, {
              slug: createdCircle.slug,
            })
          )
        }
      }
    } catch (error) {
      captureEvent('community_creation_error', {
        community_name: input.name,
        community_type: input.type,
        community_description: input.description,
        community_address: input.address,
        has_logo: !!logo,
        has_cover: !!cover,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      })

      toast.error(t('toasts:communities.new.error'))
    }
  }

  const steps: Step[] = [
    {
      id: 1,
      title: t('communities.new.steps.identity.title'),
      description: t('communities.new.steps.identity.description'),
      component: (
        <VStack gap={8} w="full" maxW="xl" align="stretch">
          <NameStep />
          <CommunityTypeField />
          <LocationStep />
        </VStack>
      ),
    },
    {
      id: 2,
      title: t('communities.new.steps.details.title'),
      description: t('communities.new.steps.details.description'),
      component: (
        <VStack gap={8} w="full" align="stretch">
          <MediaStep />
          <DetailsStep />
        </VStack>
      ),
    },
  ]

  const handleNext = async () => {
    const isValid = await methods.trigger()
    if (isValid) {
      if (currentStep === steps.length - 1) {
        await handleSubmit(methods.getValues())
      } else {
        setCurrentStep((prev) => prev + 1)
      }
    }
  }

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1)
  }

  // Page variant: full-screen diptych layout, mirroring the network wizard
  // (NetworkNewWizardGeneralStep). Modals keep the compact layout below.
  if (variant === 'page') {
    const activeStep = steps[currentStep]
    const isLastStep = currentStep === steps.length - 1

    return (
      <FormProvider {...methods}>
        <StepWizardProvider
          currentStep={currentStep + 1}
          setCurrentStep={(s) => setCurrentStep(s - 1)}
          totalSteps={steps.length}
        >
          <StepperDiptychLayout
            title={title && currentStep === 0 ? title : activeStep.title}
            description={activeStep.description}
            shellGradient
            shellGradientVariant="blue"
            rightContentAlignment="center"
            leftElement={
              <Stack
                gap={{ base: 6, md: 8 }}
                w="full"
                maxW={isLastStep ? '2xl' : 'xl'}
                alignSelf="stretch"
              >
                {activeStep.component}
                <Flex justify="center" pt={{ base: 4, md: 6 }}>
                  <PrevNextNav
                    prev={
                      currentStep > 0
                        ? {
                            onClick: handlePrev,
                            disabled: loading,
                            title: t('global:previous'),
                          }
                        : undefined
                    }
                    next={{
                      onClick: handleNext,
                      loading,
                      disabled: loading,
                      colorPalette: 'blue',
                      title: isLastStep ? t('global:create') : t('global:next'),
                    }}
                  />
                </Flex>
              </Stack>
            }
            rightElement={<WizardLivePreview />}
          />
        </StepWizardProvider>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...methods}>
      <Container py={{ base: '16', md: '24' }}>
        <Flex
          gap="10"
          w="full"
          align="flex-start"
          justify="center"
          direction={{ base: 'column', lg: 'row' }}
        >
          <VStack flex="1" gap="8" w="full">
            <VStack gap="1" textAlign="center">
              {title && currentStep === 0 ? (
                <Heading
                  fontSize={{ base: '2xl', md: '3xl' }}
                  fontWeight="bold"
                >
                  {title}
                </Heading>
              ) : (
                <Heading
                  fontSize={{ base: '2xl', md: '3xl' }}
                  fontWeight="bold"
                >
                  {steps[currentStep].title}
                </Heading>
              )}
              <Text color="fg.muted">{steps[currentStep].description}</Text>
            </VStack>
            <Flex gap="4" w="full" justifyContent="center">
              {steps[currentStep].component}
            </Flex>
            <HStack gap="4" justify="center">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  disabled={loading}
                >
                  <LuChevronLeft /> {t('global:previous')}
                </Button>
              )}
              {currentStep === steps.length - 1 ? (
                <Button
                  colorPalette="blue"
                  onClick={handleNext}
                  loading={loading}
                >
                  {t('global:create')}
                </Button>
              ) : (
                <Button
                  colorPalette="blue"
                  onClick={handleNext}
                  loading={loading}
                >
                  {t('global:next')} <LuChevronRight />
                </Button>
              )}
            </HStack>
            <HStack textStyle="sm" color="fg.muted" gap="1">
              {t('global:stepXonY', {
                x: currentStep + 1,
                y: steps.length,
              })}
            </HStack>
          </VStack>
          {showPreview && (
            <Box flex="1" hideBelow="lg" position="sticky" top="24" w="full">
              <WizardLivePreview />
            </Box>
          )}
        </Flex>
      </Container>
    </FormProvider>
  )
}
